import { useUsersLookup } from '@/components/access/users/hooks';
import { getInitials } from '@/shared/lib/names';
import type { User } from '@/shared/types/user';
import { Avatar } from '@/components/ui';
import { getAvatarColorClass } from '@/shared/lib/avatar';

/** Two-character initials for avatars (matches People 2 style). */
function getTwoCharInitials(displayName: string): string {
  const raw = getInitials(displayName);

  if (raw.length >= 2) {
    return raw.slice(0, 2);
  }

  const fallback = (displayName || '?').trim().slice(0, 2).toUpperCase();

  return (fallback + (fallback[0] || '?')).slice(0, 2);
}

export function UserItem({
  item,
  className,
  email = true,
  size = 'sm',
  fontSize = 'sm',
  fontWeight = 'normal',
  hashColor = true,
}: {
  item: User;
  className?: string;
  email?: boolean;
  size?: 'sm' | 'xs';
  fontSize?: 'sm' | 'md' | 'lg';
  fontWeight?: 'normal' | 'medium' | 'bold';
  hashColor?: boolean;
}) {
  const nameClasses =
    { sm: 'text-small', md: 'text-medium', lg: 'text-large' }[fontSize] +
    ' ' +
    { normal: 'font-normal', medium: 'font-medium', bold: 'font-bold' }[
      fontWeight
    ];
  const displayName = item.data.displayName;
  const initials = getTwoCharInitials(displayName);

  return (
    <div className={`flex items-center gap-2 ${className || ''}`}>
      <Avatar
        alt={displayName}
        name={displayName}
        getInitials={() => initials}
        className={`flex-shrink-0 ${hashColor ? getAvatarColorClass(displayName) : ''} ${size === 'xs' ? 'h-6 w-6 text-xs' : ''}`}
        size={size === 'xs' ? undefined : size}
      />
      <div className='flex flex-col'>
        <span className={nameClasses}>{item.data.displayName}</span>
        {email && (
          <span className='text-tiny text-default-500'>{item.data.email}</span>
        )}
      </div>
    </div>
  );
}

export function UserSet({
  ids,
  render = 'item',
}: {
  ids: string[];
  render?: 'text' | 'item';
}) {
  const lookup = useUsersLookup();
  const users = ids
    .map((id) => {
      const user = lookup[id] || undefined;

      return user;
    })
    .filter((user) => Boolean(user));
  if (render === 'text') {
    const text = users.map((user) => user.data.displayName).join(', ');

    return <div>{text}</div>;
  }

  return (
    <div className='flex flex-wrap gap-1'>
      {users.map((user) => (
        <UserItem
          key={user.id}
          item={user}
          email={false}
          size='xs'
          className='rounded-full bg-default-100 p-1 pr-3'
        />
      ))}
    </div>
  );
}
